/**
 * Renta Financial Calculations
 *
 * Handles affordability checks, rent calculations, and financial validations
 */

import { AffordabilityCheck, RiskScore } from '../types'

// Affordability Constants
export const AFFORDABILITY_LIMITS = {
  MIN_RATIO: 0.25, // 25% minimum
  MAX_RATIO: 0.4, // 40% maximum (strict limit)
  RECOMMENDED_RATIO: 0.35, // 35% recommended
  DEBT_TO_INCOME_MAX: 0.5, // 50% max total debt-to-income
}

// Renta Fee Structure
export const RENTA_FEE_PERCENTAGE = 0.25 // 25% of principal

// Payment Retry Configuration
export const PAYMENT_RETRY_CONFIG = {
  MAX_ATTEMPTS: 3,
  RETRY_DELAYS: [2, 5], // Days after initial failure
  GRACE_PERIOD_DAYS: 5,
  LATE_FEE_PERCENTAGE: 0.05, // 5% late fee
  DEFAULT_THRESHOLD_DAYS: 15,
}

/**
 * Get status color for UI elements
 */
export function getStatusColor(status: string): string {
  const statusColors: Record<string, string> = {
    // Property statuses
    draft: 'gray',
    pending: 'yellow',
    verified: 'green',
    rejected: 'red',
    occupied: 'blue',
    suspended: 'orange',

    // Payment statuses
    paid: 'green',
    due: 'yellow',
    overdue: 'red',
    failed: 'red',
    processing: 'blue',
    retrying: 'orange',

    // Verification statuses
    not_started: 'gray',
    in_progress: 'blue',
    pending_review: 'yellow',
    expired: 'red',

    // Request statuses
    submitted: 'blue',
    under_review: 'yellow',
    approved: 'green',
    active: 'green',
    completed: 'green',
    defaulted: 'red',
    terminated: 'orange',
    cancelled: 'gray',

    // Deduction statuses
    authorized: 'green',
    processing_deduction: 'blue',
    deducted: 'green',
    failed_deduction: 'red',
  }

  return statusColors[status] || 'gray'
}

/**
 * Calculate if a tenant can afford a property
 */
export function calculateAffordability(
  monthlyIncome: number,
  annualRent: number,
  existingDebts: number = 0,
): AffordabilityCheck {
  // Calculate monthly rent payment (including Renta fee)
  const principal = annualRent
  const rentaFee = principal * RENTA_FEE_PERCENTAGE
  const totalRepayment = principal + rentaFee
  const monthlyRent = totalRepayment / 12

  // Calculate ratios
  const rentToIncomeRatio = monthlyRent / monthlyIncome
  const debtToIncomeRatio = (monthlyRent + existingDebts) / monthlyIncome

  // Calculate max affordable rent
  const maxAffordableRent = monthlyIncome * AFFORDABILITY_LIMITS.MAX_RATIO

  // Determine affordability
  const isAffordable =
    rentToIncomeRatio <= AFFORDABILITY_LIMITS.MAX_RATIO &&
    debtToIncomeRatio <= AFFORDABILITY_LIMITS.DEBT_TO_INCOME_MAX

  // Generate recommendation
  let recommendation = ''
  if (isAffordable) {
    if (rentToIncomeRatio <= AFFORDABILITY_LIMITS.RECOMMENDED_RATIO) {
      recommendation =
        'Excellent affordability. This rent is well within your budget.'
    } else {
      recommendation =
        'Affordable, but close to your limit. Ensure you have emergency savings.'
    }
  } else {
    if (rentToIncomeRatio > AFFORDABILITY_LIMITS.MAX_RATIO) {
      recommendation = `Monthly payment exceeds ${AFFORDABILITY_LIMITS.MAX_RATIO * 100}% of income. Consider properties under ₦${Math.floor(maxAffordableRent).toLocaleString()}/month.`
    } else {
      recommendation =
        'Total debt-to-income ratio too high. Consider reducing existing debts first.'
    }
  }

  return {
    tenantMonthlyIncome: monthlyIncome,
    proposedMonthlyRent: monthlyRent,
    rentToIncomeRatio,
    maxAffordableRent,
    isAffordable,
    existingDebts,
    debtToIncomeRatio,
    recommendation,
  }
}

/**
 * Calculate rent breakdown with Renta fee
 */
export function calculateRentBreakdown(
  annualRent: number,
  duration: number = 12,
) {
  const principal = annualRent
  const rentaFee = principal * RENTA_FEE_PERCENTAGE
  const totalRepayment = principal + rentaFee
  const monthlyInstallment = totalRepayment / duration

  return {
    principal,
    rentaFee,
    totalRepayment,
    duration,
    monthlyInstallment,
    effectiveAPR: (rentaFee / principal) * (12 / duration) * 100, // Annualized percentage rate
  }
}

/**
 * Generate payment schedule
 */
export function generatePaymentSchedule(
  startDate: Date,
  monthlyAmount: number,
  duration: number = 12,
) {
  const schedule = []

  for (let i = 0; i < duration; i++) {
    const dueDate = new Date(startDate)
    dueDate.setMonth(dueDate.getMonth() + i)

    schedule.push({
      number: i + 1,
      dueDate: dueDate.toISOString().split('T')[0],
      amount: monthlyAmount,
      status: 'pending' as const,
    })
  }

  return schedule
}

/**
 * Calculate late fee
 */
export function calculateLateFee(amount: number, daysOverdue: number): number {
  if (daysOverdue <= PAYMENT_RETRY_CONFIG.GRACE_PERIOD_DAYS) {
    return 0
  }
  return amount * PAYMENT_RETRY_CONFIG.LATE_FEE_PERCENTAGE
}

/**
 * Calculate early termination settlement
 */
export function calculateEarlyTermination(
  totalRepayment: number,
  monthlyInstallment: number,
  paidInstallments: number,
  totalInstallments: number,
  securityDeposit: number,
) {
  const remainingInstallments = totalInstallments - paidInstallments
  const outstandingBalance = monthlyInstallment * remainingInstallments

  // Early termination fee: 10% of outstanding balance or ₦50,000, whichever is higher
  const earlyTerminationFee = Math.max(outstandingBalance * 0.1, 50000)

  const totalDue = outstandingBalance + earlyTerminationFee
  const securityDepositRefund = Math.max(0, securityDeposit - totalDue)
  const finalAmount = Math.max(0, totalDue - securityDeposit)

  return {
    remainingInstallments,
    outstandingBalance,
    earlyTerminationFee,
    securityDepositRefund,
    totalDue: finalAmount,
    breakdown: {
      outstandingBalance,
      earlyTerminationFee,
      subtotal: totalDue,
      securityDepositApplied: Math.min(securityDeposit, totalDue),
      finalAmountDue: finalAmount,
    },
  }
}

/**
 * Calculate risk score based on multiple factors
 */
export function calculateRiskScore(
  incomeStability: number, // 0-100
  paymentHistory: number, // 0-100
  employmentType: 'Permanent' | 'Contract' | 'Freelance',
  creditScore: number, // 300-850
  debtToIncome: number, // 0-1
): RiskScore {
  // Employment type weights
  const employmentWeights = {
    Permanent: 1.0,
    Contract: 0.8,
    Freelance: 0.6,
  }

  // Normalize credit score to 0-100
  const normalizedCreditScore = ((creditScore - 300) / (850 - 300)) * 100

  // Normalize debt-to-income (inverse - lower is better)
  const normalizedDebtToIncome = (1 - Math.min(debtToIncome, 1)) * 100

  // Calculate weighted risk score
  const weights = {
    incomeStability: 0.35,
    paymentHistory: 0.35,
    employment: 0.2,
    creditScore: 0.1,
  }

  const employmentScore = employmentWeights[employmentType] * 100

  const weightedScore =
    incomeStability * weights.incomeStability +
    paymentHistory * weights.paymentHistory +
    employmentScore * weights.employment +
    normalizedCreditScore * weights.creditScore

  // Adjust for debt-to-income
  const debtAdjustment = debtToIncome > 0.4 ? 10 : 0

  // Final risk score (lower is better, so invert)
  const finalScore = Math.max(
    0,
    Math.min(100, 100 - weightedScore + debtAdjustment),
  )

  // Determine risk level
  let level: 'Low' | 'Medium' | 'High'
  if (finalScore <= 30) level = 'Low'
  else if (finalScore <= 60) level = 'Medium'
  else level = 'High'

  // Calculate confidence based on data completeness
  const confidence = Math.min(
    100,
    (incomeStability > 0 ? 25 : 0) +
      (paymentHistory > 0 ? 25 : 0) +
      (employmentType ? 25 : 0) +
      (creditScore > 300 ? 25 : 0),
  )

  // Generate recommendations
  const recommendations: string[] = []
  if (finalScore > 60) {
    recommendations.push('Consider requiring a co-signer')
    recommendations.push('Increase security deposit')
  }
  if (debtToIncome > 0.4) {
    recommendations.push('High debt-to-income ratio - monitor closely')
  }
  if (employmentType === 'Freelance') {
    recommendations.push('Freelance income - verify multiple income streams')
  }
  if (paymentHistory < 70) {
    recommendations.push(
      'Limited payment history - consider shorter lease term',
    )
  }

  return {
    score: Math.round(finalScore),
    level,
    confidence,
    factors: {
      incomeStability,
      paymentHistory,
      employmentType,
      creditScore,
      debtToIncome,
    },
    recommendations,
    calculatedAt: new Date().toISOString(),
  }
}

/**
 * Calculate payment retry schedule
 */
export function calculatePaymentRetrySchedule(failureDate: Date) {
  const retries = []

  PAYMENT_RETRY_CONFIG.RETRY_DELAYS.forEach((delay, index) => {
    const retryDate = new Date(failureDate)
    retryDate.setDate(retryDate.getDate() + delay)

    retries.push({
      attemptNumber: index + 2, // First attempt was the failure
      scheduledFor: retryDate.toISOString().split('T')[0],
      status: 'pending' as const,
    })
  })

  return retries
}

/**
 * Calculate landlord payout
 */
export function calculateLandlordPayout(annualRent: number) {
  // Landlord receives full annual rent
  // Renta retains the fee
  return {
    annualRent,
    landlordPayout: annualRent,
    rentaFee: annualRent * RENTA_FEE_PERCENTAGE,
    totalFinanced: annualRent * (1 + RENTA_FEE_PERCENTAGE),
  }
}

/**
 * Format currency (Nigerian Naira)
 */
export function formatCurrency(amount: number): string {
  return `₦${amount.toLocaleString('en-NG', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`
}

/**
 * Calculate percentage change
 */
export function calculatePercentageChange(
  current: number,
  previous: number,
): number {
  if (previous === 0) return current > 0 ? 100 : 0
  return ((current - previous) / previous) * 100
}

/**
 * Calculate days between dates
 */
export function daysBetween(date1: Date, date2: Date): number {
  const diffTime = Math.abs(date2.getTime() - date1.getTime())
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
}

/**
 * Check if payment is overdue
 */
export function isPaymentOverdue(dueDate: string): boolean {
  const due = new Date(dueDate)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return due < today
}

/**
 * Get days overdue
 */
export function getDaysOverdue(dueDate: string): number {
  if (!isPaymentOverdue(dueDate)) return 0
  const due = new Date(dueDate)
  const today = new Date()
  return daysBetween(due, today)
}

/**
 * Calculate credit score impact
 */
export function calculateCreditScoreImpact(
  onTimePayments: number,
  latePayments: number,
  defaultedPayments: number,
): number {
  let impact = 0

  // On-time payments: +2 points each
  impact += onTimePayments * 2

  // Bonus for consecutive on-time payments
  if (onTimePayments >= 6) impact += 5
  if (onTimePayments >= 12) impact += 10

  // Late payments: -5 points each
  impact -= latePayments * 5

  // Defaulted payments: -15 points each
  impact -= defaultedPayments * 15

  return impact
}

/**
 * Validate rent request
 */
export function validateRentRequest(
  tenant: { monthlyIncome: number; verificationStatus: string },
  property: { annualRent: number; status: string },
  existingDebts: number = 0,
): { valid: boolean; errors: string[] } {
  const errors: string[] = []

  // Check tenant verification
  if (tenant.verificationStatus !== 'verified') {
    errors.push('Tenant verification must be 100% complete')
  }

  // Check property availability
  if (property.status !== 'verified') {
    errors.push('Property must be verified and available')
  }

  // Check affordability
  const affordability = calculateAffordability(
    tenant.monthlyIncome,
    property.annualRent,
    existingDebts,
  )

  if (!affordability.isAffordable) {
    errors.push(affordability.recommendation)
  }

  return {
    valid: errors.length === 0,
    errors,
  }
}
